import qs from "qs"
import { PopoverClose } from "@radix-ui/react-popover"
import { useCallback, useEffect, useState, useMemo, Fragment } from "react"
import { LoaderCircleIcon, PencilIcon, PlusIcon, TrashIcon } from "lucide-react"
import { Outlet, useFetcher, useLoaderData, useRouteLoaderData } from "react-router"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"
import { hasPermission } from "@/machines/Authmachine"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"

import type { RootLoader } from "@/routes/root"
import type { Lens, EvidenceTheme, LensThresholdResponse, ThresholdItem, UsageType } from "@/utils/types"

const routeId = "evidence-theme-thresholds"
const sessionKey = "evidence-theme-thresholds-filter-storage"
const USAGE_OPTIONS = [
  { value: "dialogue", label: "Dialogue" },
  { value: "summariser", label: "Summariser" },
] as Array<{ value: UsageType; label: string }>

function useThresholdData() {
  const data = useRouteLoaderData<typeof loader>(routeId)

  return useMemo(() => {
    if (data == null) return null

    const lensOptions = data.lenses.map((lens) => lens.code)

    const groupedEvidenceThemes = data.evidenceThemes.reduce<
      Map<string, Omit<EvidenceTheme, "usage"> & { usage: string[] }>
    >((acc, cur) => {
      if (acc.has(cur.name)) acc.get(cur.name)?.usage.push(cur.usage)
      else acc.set(cur.name, { ...cur, usage: [cur.usage] })

      return acc
    }, new Map())

    return {
      ...data,
      lensOptions,
      evidenceThemes: groupedEvidenceThemes,
      hasEvidenceThemes: groupedEvidenceThemes.size > 0,
      evidenceThemeNames: groupedEvidenceThemes.keys().toArray(),
      hasLenses: data.lenses.length > 0,
    }
  }, [data])
}

async function loader() {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  const projects = getProjectsByOrganisation(loadedOrganisation)
  const loadedProject =
    filters.parsed?.project == null || !projects.includes(filters.parsed.project) ? projects[0] : filters.parsed.project

  const [lensResponse, evidenceThemesResponse] = await Promise.allSettled([
    fetchAPI<Lens[]>(`/api/unityviewer/lens/${loadedOrganisation}/${loadedProject}`).then(handleResponse),
    fetchAPI<EvidenceTheme[]>(`/api/unityviewer/evidencetheme/${loadedOrganisation}/${loadedProject}`).then(
      handleResponse,
    ),
  ])

  const lenses = lensResponse.status === "fulfilled" ? lensResponse.value : []

  const knownUsages = USAGE_OPTIONS.map((usage) => usage.value)
  const evidenceThemes =
    evidenceThemesResponse.status === "fulfilled"
      ? evidenceThemesResponse.value.filter((theme) => knownUsages.includes(theme.usage))
      : []

  const selectedLensCode =
    filters.parsed?.lensCode == null ? undefined : lenses.find((lens) => lens.code === filters.parsed?.lensCode)?.code
  const selectedEvidenceTheme =
    filters.parsed?.evidenceTheme == null
      ? undefined
      : evidenceThemes.find((evidencetheme) => evidencetheme.name === filters.parsed?.evidenceTheme)?.name

  const queryString = qs.stringify(
    { lensCode: selectedLensCode, evidenceTheme: selectedEvidenceTheme },
    { skipNulls: true, addQueryPrefix: true },
  )

  const response = await fetchAPI<LensThresholdResponse[]>(
    `/api/unityviewer/evidencethemethreshold/${loadedOrganisation}/${loadedProject}${queryString}`,
  )
    .then(handleResponse)
    .catch(() => null)

  return {
    loadedOrganisation,
    projects,
    loadedProject,
    lenses,
    evidenceThemes,
    selectedLensCode,
    selectedEvidenceTheme,
    thresholds: response?.flatMap((item) =>
      item.thresholds.map((threshold) => ({ ...threshold, lensCode: item.lens.code })),
    ),
  }
}

async function action({ request }: { request: Request }) {
  try {
    const formData = await request.formData()
    const { action, organisation, project } = Object.fromEntries(formData) as Record<string, string>

    if (organisation == null || organisation === "") return { action, errorMessage: "Missing organisation" }
    if (project == null || project === "") return { action, errorMessage: "Missing project" }

    switch (action) {
      case "CREATE": {
        const evidenceThemeRaw = (formData.get("evidenceTheme") as string | null)?.trim()
        const evidenceTheme =
          evidenceThemeRaw == null || evidenceThemeRaw === "" || evidenceThemeRaw === "none" ? null : evidenceThemeRaw
        const thresholdStr = (formData.get("threshold") as string | null)?.trim()
        const usage = (formData.get("usage") as string | null)?.trim()
        const lensCode = (formData.get("lensCode") as string | null)?.trim()

        if (lensCode == null || lensCode === "") return { action, errorMessage: "Lens code is required" }
        if (thresholdStr == null || thresholdStr === "") return { action, errorMessage: "Threshold is required" }
        if (usage == null || usage === "") return { action, errorMessage: "Usage is required" }

        const threshold = parseFloat(thresholdStr)
        if (isNaN(threshold) || threshold < 0 || threshold > 1) {
          return { action, errorMessage: "Threshold must be between 0 and 1" }
        }

        try {
          await fetchAPI(`/api/unityviewer/evidencethemethreshold/${organisation}/${project}/${lensCode}`, {
            method: "POST",
            body: { evidenceTheme, threshold, usage },
          }).then(throwIfNotOk)
          return { action }
        } catch (error) {
          console.error(error)
          let errorMessage: string | undefined
          if ((error as { status: number }).status === 304) {
            errorMessage = "That threshold already exists, did you mean to edit it?"
          } else {
            errorMessage = "Error creating threshold"
          }

          return { action, errorMessage }
        }
      }

      case "DELETE_ALL": {
        const lensCode = (formData.get("lensCode") as string | null)?.trim()
        if (lensCode == null || lensCode === "") return { action, errorMessage: "Lens code is required for delete all" }

        try {
          await fetchAPI(`/api/unityviewer/evidencethemethreshold/${organisation}/${project}/${lensCode}`, {
            method: "DELETE",
          }).then(throwIfNotOk)
          return { action, key: Math.random().toString(36) }
        } catch (error) {
          console.error(error)
          return { action, errorMessage: "Error deleting all thresholds" }
        }
      }

      default:
        return { action, errorMessage: "Unknown action" }
    }
  } catch (error) {
    console.error("Action error:", error)
    return { action: "ERROR", errorMessage: "An unexpected error occurred" }
  }
}

async function mainAction({ request }: { request: Request }) {
  try {
    const formData = await request.formData()
    const { action, organisation, project } = Object.fromEntries(formData) as Record<string, string>

    const isValid = (t: string | undefined): t is string => t != null && t !== ""

    if (!isValid(organisation)) return { action, errorMessage: "Missing organisation" }
    if (!isValid(project)) return { action, errorMessage: "Missing project" }

    switch (action) {
      case "DELETE": {
        try {
          const evidenceThemeRaw = (formData.get("evidenceTheme") as string | null)?.trim()
          const evidenceTheme = evidenceThemeRaw == null || evidenceThemeRaw === "none" ? null : evidenceThemeRaw
          const usage = (formData.get("usage") as string | null)?.trim()
          const lensCode = (formData.get("lensCode") as string | null)?.trim()

          if (!isValid(usage) || !isValid(lensCode)) {
            return { action, errorMessage: "Missing required fields for deletion" }
          }

          await fetchAPI(`/api/unityviewer/evidencethemethreshold/delete/${organisation}/${project}/${lensCode}`, {
            method: "POST",
            body: { evidenceTheme, usage },
          }).then(throwIfNotOk)
          return { action }
        } catch (error) {
          console.error(error)
          return { action, errorMessage: "Couldn't delete threshold" }
        }
      }

      case "EDIT": {
        try {
          const evidenceThemeRaw = (formData.get("evidenceTheme") as string | null)?.trim()
          const evidenceTheme = evidenceThemeRaw == null || evidenceThemeRaw === "none" ? null : evidenceThemeRaw
          const usage = (formData.get("usage") as string | null)?.trim()
          const lensCode = (formData.get("lensCode") as string | null)?.trim()
          const thresholdStr = (formData.get("threshold") as string | null)?.trim()

          if (!isValid(usage) || !isValid(lensCode) || !isValid(thresholdStr)) {
            return { action, errorMessage: "Missing required fields" }
          }

          const threshold = parseFloat(thresholdStr)
          if (isNaN(threshold) || threshold < 0 || threshold > 1) {
            return { action, errorMessage: "Threshold must be between 0 and 1" }
          }

          await fetchAPI(`/api/unityviewer/evidencethemethreshold/${organisation}/${project}/${lensCode}`, {
            method: "PATCH",
            body: { evidenceTheme: evidenceTheme, usage, threshold },
          }).then(throwIfNotOk)
          return { action }
        } catch (error) {
          console.error(error)
          return { action, errorMessage: "Error updating threshold" }
        }
      }

      default:
        return { action, errorMessage: "Unknown action" }
    }
  } catch (error) {
    console.error("Main action error:", error)
    return { action: "ERROR", errorMessage: "An unexpected error occurred" }
  }
}

export function EvidenceThemeThresholds() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useThresholdData()

  const onBeforeSubmit = useCallback<OnBeforeSubmit>(
    (formData: FormData, reason: { type: "field"; name: string } | { type: "submitButton" }) => {
      if (reason.type !== "field") return

      if (reason.name === "organisation") formData.delete("project")
      if (reason.name === "project" || reason.name === "organisation") {
        formData.delete("evidenceTheme")
        formData.delete("lensCode")
      }
    },
    [],
  )

  return (
    <Fragment>
      <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">Evidence Theme Thresholds</h1>
      {rootData != null && data != null && (
        <FiltersWrapper submitOnChange sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
          {rootData.organisations.length > 1 && (
            <FiltersWrapper.Select
              name="organisation"
              label="Organisation"
              defaultValue={data.loadedOrganisation}
              items={rootData.organisations}
            />
          )}
          {data.projects.length > 1 && (
            <FiltersWrapper.Select
              name="project"
              label="Project"
              defaultValue={data.loadedProject}
              items={data.projects}
            />
          )}
          {data.hasLenses && (
            <FiltersWrapper.Select
              name="lensCode"
              label="Brand Lens"
              defaultValue={data.selectedLensCode}
              items={data.lensOptions}
              placeholder="All Lenses"
            />
          )}
          {data.hasEvidenceThemes && (
            <FiltersWrapper.Select
              name="evidenceTheme"
              label="Evidence Theme"
              defaultValue={data.selectedEvidenceTheme}
              items={data.evidenceThemeNames}
              placeholder="All Evidence Themes"
            />
          )}
          <div className="grow" />
        </FiltersWrapper>
      )}
      <div className="flex items-center justify-end gap-4">
        <EvidenceThemeThresholdsAddNew />
        <EvidenceThemeThresholdsDeleteAll />
      </div>
      <Outlet />
    </Fragment>
  )
}

function ThresholdRow({ threshold, index }: { threshold: ThresholdItem; index: number }) {
  const usageLabel = useMemo(
    () => USAGE_OPTIONS.find((opt) => opt.value === threshold.usage)?.label ?? threshold.usage,
    [threshold.usage],
  )

  return (
    <TableRow className="text-sm">
      <TableCell>{index + 1}.</TableCell>
      <TableCell>{threshold.lensCode}</TableCell>
      <TableCell>{threshold.evidenceTheme ?? "Default"}</TableCell>
      <TableCell>{threshold.threshold ?? 0}</TableCell>
      <TableCell>{usageLabel}</TableCell>
      <TableCell className="space-x-2">
        <EvidenceThemeThresholdEdit threshold={threshold} />
        <EvidenceThemeThresholdDelete threshold={threshold} />
      </TableCell>
    </TableRow>
  )
}

function Main() {
  const data = useLoaderData<typeof loader>()

  const tableContent = useMemo(() => {
    if (data.thresholds == null || data.thresholds.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={10} className="py-8 text-center text-gray-500">
            No thresholds found for the selected filters.
          </TableCell>
        </TableRow>
      )
    }

    return data.thresholds.map((threshold, index) => (
      <ThresholdRow
        key={`threshold-${threshold.lensCode}-${threshold.evidenceTheme ?? "default"}-${threshold.usage}-${index}`}
        threshold={threshold}
        index={index}
      />
    ))
  }, [data])

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <span className="sr-only">No.</span>
          </TableHead>
          <TableHead className="text-left">Lens</TableHead>
          <TableHead className="text-left">Evidence Theme</TableHead>
          <TableHead className="text-left">Threshold</TableHead>
          <TableHead className="text-left">Usage</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{tableContent}</TableBody>
    </Table>
  )
}

function EvidenceThemeThresholdsAddNew() {
  const data = useThresholdData()
  const [open, setOpen] = useState(false)
  const closeDialog = useCallback(() => {
    setOpen(false)
  }, [])

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon /> Add Threshold
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Evidence Theme Threshold</DialogTitle>
        </DialogHeader>
        <EvidenceThemeThresholdsAddNewForm closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function EvidenceThemeThresholdsAddNewForm({ closeDialog }: { closeDialog: () => void }) {
  const fetcher = useFetcher<typeof action>()
  const data = useThresholdData()
  const [createAnother, setCreateAnother] = useState(false)
  const [formKey, setFormKey] = useState<string>()
  const [selectedTheme, setSelectedTheme] = useState<"none" | (string & {})>("none")

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.action !== "CREATE" || fetcher.data?.errorMessage != null) return

    if (createAnother) {
      setFormKey(Math.random().toString(36))
      setSelectedTheme("none")
    } else {
      closeDialog()
    }
  }, [fetcher.state, fetcher.data, createAnother, closeDialog])

  const availableUsages = useMemo(() => {
    if (selectedTheme === "none") return USAGE_OPTIONS

    const availableEvidenceUsages = data?.evidenceThemes.get(selectedTheme)?.usage ?? []
    const alreadyCreatedThresholds = data?.thresholds?.flatMap((threshold) =>
      threshold.evidenceTheme === selectedTheme ? threshold.usage : [],
    )

    if (alreadyCreatedThresholds == null) return USAGE_OPTIONS
    return USAGE_OPTIONS.filter(
      (usage) => availableEvidenceUsages?.includes(usage.value) && !alreadyCreatedThresholds.includes(usage.value),
    )
  }, [data?.evidenceThemes, data?.thresholds, selectedTheme])

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="CREATE" />
      <input type="hidden" name="organisation" value={data?.loadedOrganisation ?? ""} />
      <input type="hidden" name="project" value={data?.loadedProject ?? ""} />
      <div>
        <label className="text-sm font-medium">Brand Lens</label>
        <Select key={formKey} required name="lensCode" defaultValue={data?.selectedLensCode}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Brand Lens" />
          </SelectTrigger>
          <SelectContent>
            {data?.lenses.map((lens) => (
              <SelectItem key={lens.code} value={lens.code}>
                {lens.name} ({lens.code})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium">Evidence Theme (Optional)</label>
        <Select key={formKey} name="evidenceTheme" value={selectedTheme} onValueChange={setSelectedTheme}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Evidence Theme (Optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Default (No specific theme)</SelectItem>
            {data?.evidenceThemeNames.map((theme) => (
              <SelectItem key={theme} value={theme}>
                {theme}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium">Threshold (0-1)</label>
        <Input
          key={formKey}
          required
          type="number"
          name="threshold"
          placeholder="Enter threshold value (0-1)"
          min="0"
          max="1"
          step="0.01"
          className="w-full"
        />
        <p className="mt-1 text-xs text-gray-500">Must be between 0 and 1 (e.g., 0.75 for 75%)</p>
      </div>
      <div>
        <label className="text-sm font-medium">Usage</label>
        <Select key={`${formKey}-${selectedTheme}`} required name="usage">
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Usage" />
          </SelectTrigger>
          <SelectContent>
            {availableUsages.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
            {availableUsages.length < 1 && (
              <p className="grid place-items-center py-3 text-sm text-muted-foreground">No usages available</p>
            )}
          </SelectContent>
        </Select>
        {availableUsages.length < USAGE_OPTIONS.length && (
          <p className="mt-1 text-xs text-gray-500">
            Only showing usages available for &quot;{selectedTheme}&quot; theme
          </p>
        )}
      </div>
      <div className="flex items-center space-x-2 border-t pt-2">
        <input
          type="checkbox"
          id="create-another"
          checked={createAnother}
          onChange={(e) => setCreateAnother(e.target.checked)}
          className="rounded border border-input"
        />
        <label htmlFor="create-another" className="text-sm leading-none font-medium">
          Create another?
        </label>
      </div>
      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Creating...
          </span>
        ) : (
          <p className="text-sm text-destructive empty:hidden">{fetcher.data?.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">Create Threshold</Button>
      </div>
    </fetcher.Form>
  )
}

function EvidenceThemeThresholdsDeleteAll() {
  const fetcher = useFetcher<typeof action>()
  const data = useThresholdData()

  if (data == null || !hasPermission("deleteAccess")) return null

  return (
    <Popover key={fetcher.data?.key}>
      <PopoverTrigger asChild>
        <Button variant="destructive">
          <TrashIcon /> Delete All
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end">
        {data.selectedLensCode == null ? (
          <p className="grid place-items-center text-sm text-muted-foreground">Select a Lens to Delete</p>
        ) : (
          <Fragment>
            <p className="text-sm text-pretty">
              Are you sure you want to <span className="font-semibold">DELETE ALL</span> Evidence Theme Thresholds for
              the selected lens in the &quot;<span className="font-semibold">{data.loadedOrganisation}</span>&quot;
              organisation?
            </p>
            <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
              <input type="hidden" name="action" value="DELETE_ALL" />
              <input type="hidden" name="project" value={data.loadedProject} />
              <input type="hidden" name="organisation" value={data.loadedOrganisation} />
              <input type="hidden" name="lensCode" value={data.selectedLensCode ?? ""} />
              {fetcher.state !== "idle" && (
                <span className="flex items-center justify-start gap-1 pr-4">
                  <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
                </span>
              )}
              <div className="flex-1" />
              <PopoverClose asChild>
                <Button>Cancel</Button>
              </PopoverClose>
              <Button type="submit" variant="ghost">
                Confirm
              </Button>
            </fetcher.Form>
          </Fragment>
        )}
      </PopoverContent>
    </Popover>
  )
}

function EvidenceThemeThresholdEdit({ threshold }: { threshold: ThresholdItem }) {
  const [isOpen, setIsOpen] = useState(false)

  if (!hasPermission("writeAccess")) return null

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Edit Threshold">
          <PencilIcon />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Threshold</DialogTitle>
        </DialogHeader>
        <EvidenceThemeThresholdEditForm threshold={threshold} onSuccess={() => setIsOpen(false)} />
      </DialogContent>
    </Dialog>
  )
}

function EvidenceThemeThresholdEditForm({ threshold, onSuccess }: { threshold: ThresholdItem; onSuccess: () => void }) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useThresholdData()

  const lensName = useMemo(() => {
    return data?.lenses.find((lens) => lens.code === threshold.lensCode)?.name ?? threshold.lensCode
  }, [data?.lenses, threshold.lensCode])

  const usageLabel = useMemo(() => {
    return USAGE_OPTIONS.find((option) => option.value === threshold.usage)?.label ?? threshold.usage
  }, [threshold.usage])

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.errorMessage != null) return
    onSuccess()
  }, [fetcher.state, fetcher.data, onSuccess])

  if (data == null) return null

  return (
    <div className="space-y-4">
      <div className="space-y-3 rounded-lg bg-gray-50 p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">Brand Lens:</span>
          <span className="text-sm text-gray-900">
            {lensName} ({threshold.lensCode})
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">Evidence Theme:</span>
          <span className="text-sm text-gray-900">{threshold.evidenceTheme ?? "Default"}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">Usage:</span>
          <span className="text-sm text-gray-900">{usageLabel}</span>
        </div>
      </div>
      <fetcher.Form method="PATCH" className="space-y-4">
        <input type="hidden" name="action" value="EDIT" />
        <input type="hidden" name="organisation" value={data.loadedOrganisation} />
        <input type="hidden" name="project" value={data.loadedProject} />
        <input type="hidden" name="lensCode" value={threshold.lensCode} />
        <input type="hidden" name="evidenceTheme" value={threshold.evidenceTheme ?? ""} />
        <input type="hidden" name="usage" value={threshold.usage} />
        <div>
          <label className="text-sm font-medium">Threshold (0-1)</label>
          <Input
            required
            type="number"
            name="threshold"
            defaultValue={threshold.threshold}
            min="0"
            max="1"
            step="0.01"
            className="w-full"
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500">Only the threshold value can be edited</p>
        </div>
        <div className="flex items-center justify-start gap-4">
          {fetcher.state !== "idle" ? (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" /> Updating...
            </span>
          ) : (
            <p className="text-sm text-destructive empty:hidden">{fetcher.data?.errorMessage}</p>
          )}
          <div className="flex-1" />
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button type="submit">Update Threshold</Button>
        </div>
      </fetcher.Form>
    </div>
  )
}

function EvidenceThemeThresholdDelete({ threshold }: { threshold: ThresholdItem }) {
  const usageLabel = useMemo(
    () => USAGE_OPTIONS.find((opt) => opt.value === threshold.usage)?.label ?? threshold.usage,
    [threshold.usage],
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Delete Threshold">
          <TrashIcon className="text-red-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="max-w-sm">
        <p className="text-sm text-pretty">
          Delete threshold for <span className="font-semibold">&quot;{threshold.evidenceTheme ?? "Default"}&quot;</span>{" "}
          with usage <span className="font-semibold">&quot;{usageLabel}&quot;</span>?
        </p>
        <EvidenceThemeThresholdDeleteForm threshold={threshold} />
      </PopoverContent>
    </Popover>
  )
}

function EvidenceThemeThresholdDeleteForm({ threshold }: { threshold: ThresholdItem }) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useThresholdData()

  if (data == null) return null

  return (
    <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="lensCode" value={threshold.lensCode ?? ""} />
      <input type="hidden" name="evidenceTheme" value={threshold.evidenceTheme ?? ""} />
      <input type="hidden" name="usage" value={threshold.usage} />

      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
        </span>
      ) : (
        fetcher.data?.action === "DELETE" && (
          <p className="text-sm text-destructive empty:hidden">{fetcher.data.errorMessage}</p>
        )
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost">
        Confirm
      </Button>
    </fetcher.Form>
  )
}

EvidenceThemeThresholds.loader = loader
EvidenceThemeThresholds.action = action
EvidenceThemeThresholds.Main = Main
EvidenceThemeThresholds.routeId = routeId

Main.action = mainAction
